import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { adminApi } from '../api'
import type { CuratedFeedItem } from '../api/curatedFeed'
import type { Review } from '../api/review'
import AdminSidebar from '../components/AdminSidebar'
import { toastUtil } from '../../utils/toast'

/**
 * 가입 직후 보이는 추천 피드를 직접 고른다.
 *
 * 기록이 3건 미만인 사용자에게 여기 담은 순서대로 먼저 보이고, 모자라는
 * 만큼만 기존 인기·다양성 폴백으로 채워진다. 비워두면 예전처럼 전부
 * 알고리즘이 고른다.
 *
 * 첫 화면은 인상이 결정되는 자리다. 폴백만 쓰면 서울 다음에 진주, 그다음
 * 대구가 나오고 본문이 빈 기록도 섞여 들어와 통제할 수가 없었다.
 */
const AdminCuratedFeed: React.FC = () => {
  const navigate = useNavigate()
  const [items, setItems] = useState<CuratedFeedItem[]>([])
  const [isLoading, setIsLoading] = useState(true)

  // 담을 기록을 찾는 쪽
  const [search, setSearch] = useState('')
  const [results, setResults] = useState<Review[]>([])
  const [isSearching, setIsSearching] = useState(false)

  const fetchItems = useCallback(async () => {
    try {
      setIsLoading(true)
      const response = await adminApi.curatedFeed.list()
      setItems(response.data)
    } catch (error: any) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        navigate('/admin/login')
      } else {
        toastUtil.error('목록을 불러오지 못했습니다')
      }
    } finally {
      setIsLoading(false)
    }
  }, [navigate])

  useEffect(() => {
    if (!localStorage.getItem('admin_token')) {
      navigate('/admin/login')
      return
    }
    fetchItems()
  }, [navigate, fetchItems])

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!search.trim()) return
    try {
      setIsSearching(true)
      const response = await adminApi.getReviews({ search: search.trim(), per_page: 10 })
      setResults(response.data)
    } catch {
      toastUtil.error('검색에 실패했습니다')
    } finally {
      setIsSearching(false)
    }
  }

  const handleAdd = async (reviewId: number) => {
    try {
      const response = await adminApi.curatedFeed.add(reviewId)
      toastUtil.success(response.message)
      fetchItems()
    } catch (error: any) {
      toastUtil.error(error.response?.data?.message || '추가하지 못했습니다')
    }
  }

  const handleRemove = async (id: number) => {
    try {
      const response = await adminApi.curatedFeed.remove(id)
      toastUtil.success(response.message)
      setItems((prev) => prev.filter((i) => i.id !== id))
    } catch (error: any) {
      toastUtil.error(error.response?.data?.message || '빼지 못했습니다')
    }
  }

  /** 한 칸 위/아래로. 화면에서 바꾸고 순서를 통째로 저장한다. */
  const handleMove = async (index: number, direction: -1 | 1) => {
    const next = index + direction
    if (next < 0 || next >= items.length) return

    const reordered = [...items]
    ;[reordered[index], reordered[next]] = [reordered[next], reordered[index]]
    setItems(reordered)

    try {
      await adminApi.curatedFeed.reorder(reordered.map((i) => i.id))
    } catch {
      toastUtil.error('순서를 저장하지 못했습니다')
      fetchItems()
    }
  }

  const alreadyAdded = (reviewId: number) =>
    items.some((i) => i.review_id === reviewId)

  return (
    <div className="flex min-h-screen bg-backgroundColorBg1">
      <AdminSidebar />
      {/* min-w-0 이 없으면 flex 항목이 내용 너비만큼 부풀어 화면 밖으로 넘친다 */}
      <div className="flex-1 min-w-0 p-32px">
        <h1 className="text-style-h2 text-textColorText1 mb-8px">
          가입 직후 추천 피드
        </h1>
        <p className="text-style-body2 text-textColorText3 mb-24px">
          기록이 3건 미만인 사용자에게 아래 순서대로 먼저 보입니다. 모자라는
          만큼은 기존 추천이 채웁니다. 비워두면 전부 기존 추천으로 나갑니다.
        </p>

        {/* 담은 목록 */}
        <div className="mb-40px">
          <h2 className="text-style-h3 text-textColorText1 mb-12px">
            담은 기록 {items.length}개
          </h2>
          {isLoading ? (
            <div className="py-40px text-center text-style-body2 text-textColorText3">
              불러오는 중…
            </div>
          ) : items.length === 0 ? (
            <div className="py-40px text-center text-style-body2 text-textColorText3 border border-brandPrimary-20% rounded-8px">
              아직 없습니다. 아래에서 기록을 찾아 담아주세요.
            </div>
          ) : (
            <div className="flex flex-col gap-8px">
              {items.map((item, index) => (
                <div
                  key={item.id}
                  className={`flex items-center gap-12px p-12px border rounded-8px ${
                    item.exists && item.is_public
                      ? 'border-brandPrimary-20%'
                      : 'border-[#9B4A3A] bg-[#9B4A3A]/5'
                  }`}
                >
                  <span className="shrink-0 w-28px text-style-labelS text-textColorText3">
                    {index + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    {item.exists ? (
                      <>
                        <div className="text-style-body2 text-textColorText2 truncate">
                          {item.place_name}
                          <span className="text-textColorText3 ml-8px">
                            {item.score}점 · @{item.username}
                          </span>
                          {!item.is_public && (
                            <span className="text-[#9B4A3A] ml-8px">비공개로 바뀜</span>
                          )}
                        </div>
                        {item.review_text && (
                          <div className="text-style-labelS text-textColorText3 mt-2px truncate">
                            {item.review_text}
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="text-style-body2 text-[#9B4A3A]">
                        삭제된 기록입니다 (id {item.review_id}) — 빼주세요
                      </div>
                    )}
                  </div>
                  <div className="shrink-0 flex gap-4px">
                    <button
                      type="button"
                      onClick={() => handleMove(index, -1)}
                      disabled={index === 0}
                      className="px-10px py-6px rounded-8px border border-brandPrimary-20% text-style-labelS text-textColorText2 disabled:opacity-30"
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      onClick={() => handleMove(index, 1)}
                      disabled={index === items.length - 1}
                      className="px-10px py-6px rounded-8px border border-brandPrimary-20% text-style-labelS text-textColorText2 disabled:opacity-30"
                    >
                      ↓
                    </button>
                    <button
                      type="button"
                      onClick={() => handleRemove(item.id)}
                      className="px-10px py-6px rounded-8px border border-[#9B4A3A] text-style-labelS text-[#9B4A3A]"
                    >
                      빼기
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* 찾아서 담기 */}
        <div>
          <h2 className="text-style-h3 text-textColorText1 mb-12px">기록 찾기</h2>
          <form onSubmit={handleSearch} className="flex gap-8px mb-16px">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="가게 이름, 아이디, 한줄평으로 검색"
              className="flex-1 py-10px px-12px border border-brandPrimary-20% rounded-8px text-style-body2 text-textColorText2 bg-transparent focus:outline-none focus:border-brandPrimary placeholder:text-textColorText3"
            />
            <button
              type="submit"
              disabled={isSearching}
              className="shrink-0 px-20px rounded-8px bg-4B3621-80% text-white text-style-button2 disabled:opacity-40"
            >
              {isSearching ? '찾는 중' : '검색'}
            </button>
          </form>

          <div className="flex flex-col gap-8px">
            {results.map((review) => (
              <div
                key={review.id}
                className="flex items-center gap-12px p-12px border border-brandPrimary-20% rounded-8px"
              >
                <div className="flex-1 min-w-0">
                  <div className="text-style-body2 text-textColorText2 truncate">
                    {review.place?.name}
                    <span className="text-textColorText3 ml-8px">
                      {review.score}점 · @{review.user?.username}
                    </span>
                  </div>
                  {review.review_text && (
                    <div className="text-style-labelS text-textColorText3 mt-2px truncate">
                      {review.review_text}
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => handleAdd(review.id)}
                  disabled={alreadyAdded(review.id)}
                  className="shrink-0 px-14px py-8px rounded-8px bg-4B3621-80% text-white text-style-labelS disabled:opacity-30"
                >
                  {alreadyAdded(review.id) ? '담김' : '담기'}
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

export default AdminCuratedFeed
